import {
  getPlatformGetFilterByIDTeamQueryKey,
  getPlatformGetNotificationDestinationQueryKey,
  getPlatformGetPolicyQueryKey,
  getPlatformGetSavedQueryTeamQueryKey,
  getPlatformGetSettingsQueryKey,
  getPlatformGetSyncQueryKey,
  getPlatformGetSyncRunQueryKey,
  getPlatformGetV2SyncDestinationsQueryKey,
  getPlatformListAllCustomColumnsQueryKey,
  getPlatformListAllNotificationDestinationsQueryKey,
  getPlatformListAllQueriesTeamQueryKey,
  getPlatformListLocalUsersQueryKey,
  getPlatformListPoliciesQueryKey,
  getPlatformListReportsQueryKey,
  getPlatformListSyncDestinationsQueryKey,
  getPlatformListSyncDestinationSyncsQueryKey,
  getPlatformListSyncRunsQueryKey,
  getPlatformListSyncSourcesQueryKey,
  getPlatformListSyncSourceSyncsQueryKey,
  getPlatformListSyncsQueryKey,
  getPlatformListTeamAPIKeysQueryKey,
  getPlatformListV2SyncIntegrationsQueryKey,
  getPlatformManageGetPlatformRegistryQueryKey,
  getPlatformManageListAllTeamsQueryKey,
  getPlatformTableListFiltersTeamQueryKey,
  usePlatformCreateNotificationDestination,
  usePlatformCreatePolicy,
  usePlatformCreateReport,
  usePlatformCreateSync,
  usePlatformCreateSyncRun,
  usePlatformCreateTeamAPIKey,
  usePlatformCreateV2SyncIntegration,
  usePlatformCreateV2SyncDestination,
  usePlatformDeleteCustomColumn,
  usePlatformDeleteFilterTeam,
  usePlatformDeleteNotificationDestination,
  usePlatformDeletePolicy,
  usePlatformDeleteReport,
  usePlatformDeleteSync,
  usePlatformDeleteSyncDestination,
  usePlatformDeleteSyncSource,
  usePlatformDeleteTeamAPIKey,
  usePlatformDeleteV2SyncIntegration,
  usePlatformPatchV2SyncDestination,
  usePlatformPromoteSyncDestinationTestConnection,
  usePlatformPromoteSyncSourceTestConnection,
  usePlatformSaveCustomColumn,
  usePlatformTableSaveFilterTeam,
  usePlatformUpdateCustomColumn,
  usePlatformUpdateFilterTeam,
  usePlatformUpdateNotificationDestination,
  usePlatformUpdatePolicy,
  usePlatformUpdateQueryTeam,
  usePlatformUpdateReport,
  usePlatformUpdateSettings,
  usePlatformUpdateSync,
  usePlatformUpdateSyncRun,
  usePlatformUpdateV2SyncIntegration,
  getPlatformGetV2SyncIntegrationQueryKey,
  usePlatformUpdateRBACPermission,
  usePlatformCreateRBACPermission,
  usePlatformDeleteRBACPermission,
  getPlatformGetRBACPermissionQueryKey,
  getPlatformListAllRBACPermissionsQueryKey,
  getPlatformListAllRBACRolesQueryKey,
  getPlatformGetRBACRoleQueryKey,
  usePlatformCreateRBACRole,
  usePlatformUpdateRBACRole,
  usePlatformDeleteRBACRole,
  getPlatformManageListTeamMembersQueryKey,
  usePlatformManageAddTeamMember,
} from '@cloudquery/api-platform';
import { Query, QueryKey } from '@tanstack/react-query';

import { MutationOperationConfigKey } from './mutationOperationConfigKey';

type ExtractPayload<Hook extends (...args: any[]) => any> = Parameters<
  ReturnType<Hook>['mutateAsync']
>[0];

type MessageParams<HookType extends (...args: any[]) => any> = {
  payload: ExtractPayload<HookType>;
};

type InvalidateQueryParams<HookType extends (...args: any[]) => any> = {
  cachedQueries: Query[];
  payload: ExtractPayload<HookType>;
};

/* eslint-disable sort-keys-fix/sort-keys-fix */
export const mutationOperationConfig: {
  [_ in MutationOperationConfigKey]:
    | {
        errorMessage: string | ((data: { payload: any }) => string | null) | null;
        invalidateQueries: (
          | QueryKey
          | ((data: any) => QueryKey | { queryKeys: QueryKey[] } | undefined)
        )[];
        successMessage: string | ((data: { payload: any }) => string | null) | null;
      }
    | undefined;
} = {
  platformCreateNotificationDestination: {
    errorMessage: 'Failed to create notification destination',
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformCreateNotificationDestination>) =>
        getPlatformListAllNotificationDestinationsQueryKey(payload.teamName),
    ],
    successMessage: 'Notification destination has been created',
  },
  platformCreateTeam: {
    errorMessage: 'Failed to create workspace',
    invalidateQueries: [getPlatformManageListAllTeamsQueryKey()],
    successMessage: 'Workspace has been created',
  },
  platformUpdateTeam: {
    errorMessage: 'Failed to update workspace',
    invalidateQueries: [getPlatformManageListAllTeamsQueryKey()],
    successMessage: 'Workspace has been updated',
  },
  platformDeleteTeam: {
    errorMessage: 'Failed to delete workspace',
    invalidateQueries: [getPlatformManageListAllTeamsQueryKey()],
    successMessage: 'Workspace has been deleted',
  },
  platformUpdateCurrentUser: {
    errorMessage: 'Failed to update user',
    invalidateQueries: [],
    successMessage: 'User has been updated',
  },
  platformCreateRBACPermission: {
    errorMessage: 'Failed to create data permission',
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformCreateRBACPermission>) =>
        getPlatformListAllRBACPermissionsQueryKey(payload.teamName),
    ],
    successMessage: 'Data permission has been created',
  },
  platformUpdateRBACPermission: {
    errorMessage: 'Failed to update data permission',
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformUpdateRBACPermission>) =>
        getPlatformListAllRBACPermissionsQueryKey(payload.teamName),
      ({ payload }: InvalidateQueryParams<typeof usePlatformUpdateRBACPermission>) =>
        getPlatformGetRBACPermissionQueryKey(payload.teamName, payload.permissionId),
    ],
    successMessage: 'Data permission has been updated',
  },
  platformDeleteRBACPermission: {
    errorMessage: 'Failed to delete data permission',
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformDeleteRBACPermission>) =>
        getPlatformListAllRBACPermissionsQueryKey(payload.teamName),
    ],
    successMessage: 'Data permission has been deleted',
  },
  platformCreateRBACRole: {
    errorMessage: 'Failed to create role',
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformCreateRBACRole>) =>
        getPlatformListAllRBACRolesQueryKey(payload.teamName),
    ],
    successMessage: 'Role has been created',
  },
  platformUpdateRBACRole: {
    errorMessage: 'Failed to update role',
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformUpdateRBACRole>) =>
        getPlatformListAllRBACRolesQueryKey(payload.teamName),
      ({ payload }: InvalidateQueryParams<typeof usePlatformUpdateRBACRole>) =>
        getPlatformGetRBACRoleQueryKey(payload.teamName, payload.roleId),
    ],
    successMessage: 'Role has been updated',
  },
  platformDeleteRBACRole: {
    errorMessage: 'Failed to delete role',
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformDeleteRBACRole>) =>
        getPlatformListAllRBACRolesQueryKey(payload.teamName),
    ],
    successMessage: 'Role has been deleted',
  },
  platformCreateTeamAPIKey: {
    errorMessage: 'Failed to create API key',
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformCreateTeamAPIKey>) =>
        getPlatformListTeamAPIKeysQueryKey(payload.teamName),
    ],
    successMessage: 'API key has been created',
  },
  platformDeleteNotificationDestination: {
    errorMessage: 'Failed to delete notification destination',
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformDeleteNotificationDestination>) =>
        getPlatformListAllNotificationDestinationsQueryKey(payload.teamName),
    ],
    successMessage: 'Notification destination has been deleted',
  },
  platformDeleteTeamAPIKey: {
    errorMessage: 'Failed to delete API key',
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformDeleteTeamAPIKey>) =>
        getPlatformListTeamAPIKeysQueryKey(payload.teamName),
    ],
    successMessage: 'API key has been deleted',
  },
  platformPromoteSyncSourceTestConnection: {
    errorMessage: null,
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformPromoteSyncSourceTestConnection>) =>
        getPlatformListSyncSourcesQueryKey(payload.teamName),
    ],
    successMessage: ({
      payload,
    }: MessageParams<typeof usePlatformPromoteSyncSourceTestConnection>) =>
      payload.data.overwrite_source
        ? `Integration has been updated`
        : `Integration has been created`,
  },
  platformPromoteSyncDestinationTestConnection: {
    errorMessage: null,
    invalidateQueries: [
      ({
        payload,
      }: InvalidateQueryParams<typeof usePlatformPromoteSyncDestinationTestConnection>) =>
        getPlatformListSyncDestinationsQueryKey(payload.teamName),
    ],
    successMessage: ({
      payload,
    }: MessageParams<typeof usePlatformPromoteSyncDestinationTestConnection>) =>
      payload.data.overwrite_destination
        ? `Destination has been updated`
        : `Destination has been created`,
  },
  platformCreateV2SyncDestination: {
    errorMessage: null,
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformCreateV2SyncDestination>) =>
        getPlatformGetV2SyncDestinationsQueryKey(payload.teamName),
    ],
    successMessage: ({
      payload,
    }: MessageParams<typeof usePlatformPromoteSyncDestinationTestConnection>) =>
      payload.data.overwrite_destination
        ? `Destination has been updated`
        : `Destination has been created`,
  },
  platformDeleteSyncSource: {
    errorMessage: 'Failed to delete integration',
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformDeleteSyncSource>) =>
        getPlatformListSyncSourcesQueryKey(payload.teamName),
    ],
    successMessage: 'Integration has been deleted',
  },
  platformDeleteSyncDestination: {
    errorMessage: 'Failed to delete destination',
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformDeleteSyncDestination>) =>
        getPlatformListSyncDestinationsQueryKey(payload.teamName),
    ],
    successMessage: 'Destination has been deleted',
  },
  platformDeleteV2SyncDestination: {
    errorMessage: 'Failed to delete destination',
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformDeleteSyncDestination>) =>
        getPlatformGetV2SyncDestinationsQueryKey(payload.teamName),
    ],
    successMessage: 'Destination has been deleted',
  },
  platformCreateSync: {
    errorMessage: 'Failed to create sync',
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformCreateSync>) =>
        getPlatformListSyncsQueryKey(payload.teamName),
      ({ cachedQueries, payload }: InvalidateQueryParams<typeof usePlatformCreateSync>) =>
        getInvalidateSyncSourceSyncsQueryKey(payload.teamName, cachedQueries),
      ({ cachedQueries, payload }: InvalidateQueryParams<typeof usePlatformCreateSync>) =>
        getInvalidateSyncDestinationSyncsQueryKey(payload.teamName, cachedQueries),
    ],
    successMessage: 'Sync has been created',
  },
  platformUpdateSync: {
    errorMessage: ({ payload }: MessageParams<typeof usePlatformUpdateSync>) => {
      // handle enable/disable sync
      if (Object.keys(payload.data).length === 1 && payload.data.disabled === true) {
        return 'Failed to disable sync';
      } else if (Object.keys(payload.data).length === 1 && payload.data.disabled === false) {
        return 'Failed to enable sync';
      }

      return 'Failed to update sync';
    },
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformUpdateSync>) =>
        getPlatformListSyncsQueryKey(payload.teamName),
      ({ payload }: InvalidateQueryParams<typeof usePlatformUpdateSync>) =>
        getPlatformGetSyncQueryKey(payload.teamName, payload.syncName),
      ({ cachedQueries, payload }: InvalidateQueryParams<typeof usePlatformUpdateSync>) =>
        getInvalidateSyncSourceSyncsQueryKey(payload.teamName, cachedQueries),
      ({ cachedQueries, payload }: InvalidateQueryParams<typeof usePlatformUpdateSync>) =>
        getInvalidateSyncDestinationSyncsQueryKey(payload.teamName, cachedQueries),
    ],
    successMessage: ({ payload }: MessageParams<typeof usePlatformUpdateSync>) => {
      if (Object.keys(payload.data).length === 1 && payload.data.disabled === true) {
        return 'Sync has been disabled';
      } else if (Object.keys(payload.data).length === 1 && payload.data.disabled === false) {
        return 'Sync has been enabled';
      }

      return 'Sync has been updated';
    },
  },
  platformDeleteSync: {
    errorMessage: 'Failed to delete sync',
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformDeleteSync>) =>
        getPlatformListSyncsQueryKey(payload.teamName),
      ({ cachedQueries, payload }: InvalidateQueryParams<typeof usePlatformDeleteSync>) =>
        getInvalidateSyncSourceSyncsQueryKey(payload.teamName, cachedQueries),
      ({ cachedQueries, payload }: InvalidateQueryParams<typeof usePlatformDeleteSync>) =>
        getInvalidateSyncDestinationSyncsQueryKey(payload.teamName, cachedQueries),
    ],
    successMessage: 'Sync has been deleted',
  },
  platformCreateSyncRun: {
    errorMessage: 'Failed to create sync run',
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformCreateSyncRun>) =>
        getPlatformListSyncsQueryKey(payload.teamName),
      ({ payload }: InvalidateQueryParams<typeof usePlatformCreateSyncRun>) =>
        getPlatformListSyncRunsQueryKey(payload.teamName, payload.syncName),
      ({ cachedQueries, payload }: InvalidateQueryParams<typeof usePlatformCreateSyncRun>) =>
        getInvalidateSyncSourceSyncsQueryKey(payload.teamName, cachedQueries),
      ({ cachedQueries, payload }: InvalidateQueryParams<typeof usePlatformCreateSyncRun>) =>
        getInvalidateSyncDestinationSyncsQueryKey(payload.teamName, cachedQueries),
    ],
    successMessage: 'Sync run has been created',
  },
  platformUpdateSyncRun: {
    errorMessage: ({ payload }: MessageParams<typeof usePlatformUpdateSyncRun>) => {
      return payload.data.status === 'cancelled' ? 'Failed to cancel sync run' : null;
    },
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformUpdateSyncRun>) =>
        getPlatformListSyncsQueryKey(payload.teamName),
      ({ cachedQueries, payload }: InvalidateQueryParams<typeof usePlatformUpdateSyncRun>) =>
        getInvalidateSyncSourceSyncsQueryKey(payload.teamName, cachedQueries),
      ({ cachedQueries, payload }: InvalidateQueryParams<typeof usePlatformUpdateSyncRun>) =>
        getInvalidateSyncDestinationSyncsQueryKey(payload.teamName, cachedQueries),
      ({ payload }: InvalidateQueryParams<typeof usePlatformUpdateSyncRun>) =>
        getPlatformListSyncRunsQueryKey(payload.teamName, payload.syncName),
      ({ payload }: InvalidateQueryParams<typeof usePlatformUpdateSyncRun>) =>
        getPlatformGetSyncRunQueryKey(payload.teamName, payload.syncName, payload.syncRunId),
    ],
    successMessage: ({ payload }: MessageParams<typeof usePlatformUpdateSyncRun>) => {
      return payload.data.status === 'cancelled' ? 'Sync run has been cancelled' : null;
    },
  },
  platformChangeLocalUserPassword: {
    errorMessage: 'Failed to change password',
    invalidateQueries: [],
    successMessage: 'Password has been changed',
  },
  platformManageRegisterPlatform: {
    errorMessage: 'Failed to save API key',
    invalidateQueries: [getPlatformManageGetPlatformRegistryQueryKey()],
    successMessage: 'API key has been saved',
  },
  platformManageRegisterPlatformWithOfflineLicense: {
    errorMessage: 'Failed to upload offline license',
    invalidateQueries: [getPlatformManageGetPlatformRegistryQueryKey()],
    successMessage: 'Offline license has been uploaded',
  },
  platformManageDeleteTeam: {
    errorMessage: 'Failed to delete workspace',
    invalidateQueries: [
      getPlatformManageListAllTeamsQueryKey(),
      getPlatformListLocalUsersQueryKey(),
    ],
    successMessage: 'Workspace has been deleted',
  },
  platformManageAddTeamMember: {
    errorMessage: 'Failed to add user to workspace',
    invalidateQueries: [
      getPlatformManageListAllTeamsQueryKey(),
      getPlatformListLocalUsersQueryKey(),
      ({ payload }: InvalidateQueryParams<typeof usePlatformManageAddTeamMember>) =>
        getPlatformManageListTeamMembersQueryKey(payload.teamName),
    ],
    successMessage: 'User has been added to workspace',
  },
  platformManageRemoveTeamMember: {
    errorMessage: 'Failed to remove user from workspace',
    invalidateQueries: [
      getPlatformManageListAllTeamsQueryKey(),
      getPlatformListLocalUsersQueryKey(),
    ],
    successMessage: 'User has been removed from workspace',
  },
  platformPatchV2SyncDestination: {
    errorMessage: 'Failed to update destination',
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformPatchV2SyncDestination>) =>
        getPlatformGetV2SyncDestinationsQueryKey(payload.teamName),
    ],
    successMessage: 'Destination has been updated',
  },
  platformAddLocalUser: {
    errorMessage: 'Failed to create user',
    invalidateQueries: [getPlatformListLocalUsersQueryKey()],
    successMessage: 'User has been created',
  },
  platformUpdateLocalUser: {
    errorMessage: 'Failed to update user',
    invalidateQueries: [getPlatformListLocalUsersQueryKey()],
    successMessage: 'User has been updated',
  },
  platformDeleteLocalUser: {
    errorMessage: 'Failed to delete user',
    invalidateQueries: [getPlatformListLocalUsersQueryKey()],
    successMessage: 'User has been deleted',
  },
  platformMigrateSyncDestination: {
    errorMessage: 'Failed to migrate sync destination',
    invalidateQueries: [],
    successMessage: 'Migration started successfully',
  },
  platformMigrateSyncSource: {
    errorMessage: 'Failed to migrate sync source',
    invalidateQueries: [],
    successMessage: 'Migration started successfully',
  },
  platformSaveQueryTeam: {
    errorMessage: 'Failed to save query',
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformUpdateQueryTeam>) =>
        getPlatformListAllQueriesTeamQueryKey(payload.teamName),
      ({ payload }: InvalidateQueryParams<typeof usePlatformUpdateQueryTeam>) =>
        getPlatformGetSavedQueryTeamQueryKey(payload.teamName, payload.queryId),
    ],
    successMessage: 'Query has been saved',
  },
  platformUpdateNotificationDestination: {
    errorMessage: 'Failed to update notification destination',
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformUpdateNotificationDestination>) =>
        getPlatformListAllNotificationDestinationsQueryKey(payload.teamName),
      ({ payload }: InvalidateQueryParams<typeof usePlatformUpdateNotificationDestination>) =>
        getPlatformGetNotificationDestinationQueryKey(
          payload.teamName,
          payload.notificationDestinationId,
        ),
    ],
    successMessage: 'Notification destination has been updated',
  },
  platformUpdateQueryTeam: {
    errorMessage: 'Failed to update query',
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformUpdateQueryTeam>) =>
        getPlatformListAllQueriesTeamQueryKey(payload.teamName),
      ({ payload }: InvalidateQueryParams<typeof usePlatformUpdateQueryTeam>) =>
        getPlatformGetSavedQueryTeamQueryKey(payload.teamName, payload.queryId),
    ],
    successMessage: 'Query has been updated',
  },
  platformDeleteSavedQueryTeam: {
    errorMessage: 'Failed to delete saved query',
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformUpdateQueryTeam>) =>
        getPlatformListAllQueriesTeamQueryKey(payload.teamName),
    ],
    successMessage: 'Query has been deleted',
  },
  platformTableSaveFilterTeam: {
    errorMessage: 'Failed to save filter',
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformTableSaveFilterTeam>) =>
        getPlatformTableListFiltersTeamQueryKey(payload.teamName, payload.tableName),
    ],
    successMessage: 'Filter has been saved',
  },
  platformUpdateFilterTeam: {
    errorMessage: null,
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformUpdateFilterTeam>) =>
        getPlatformTableListFiltersTeamQueryKey(payload.teamName, payload.filterId),
      ({ payload }: InvalidateQueryParams<typeof usePlatformUpdateFilterTeam>) =>
        getPlatformGetFilterByIDTeamQueryKey(payload.teamName, payload.filterId),
    ],
    successMessage: 'Filter has been updated',
  },
  platformDeleteFilterTeam: {
    errorMessage: 'Failed to delete filter',
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformDeleteFilterTeam>) =>
        getPlatformTableListFiltersTeamQueryKey(payload.teamName, payload.filterId),
    ],
    successMessage: 'Filter has been deleted',
  },
  platformCreatePolicy: {
    errorMessage: 'Failed to create policy',
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformCreatePolicy>) =>
        getPlatformListPoliciesQueryKey(payload.teamName),
    ],
    successMessage: 'Policy has been created',
  },
  platformUpdatePolicy: {
    errorMessage: 'Error updating policy',
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformUpdatePolicy>) =>
        getPlatformListPoliciesQueryKey(payload.teamName),
      ({ payload }: InvalidateQueryParams<typeof usePlatformUpdatePolicy>) =>
        getPlatformGetPolicyQueryKey(payload.teamName, payload.policyId),
    ],
    successMessage: 'Policy has been updated',
  },
  platformDeletePolicy: {
    errorMessage: 'Error deleting policy',
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformDeletePolicy>) =>
        getPlatformListPoliciesQueryKey(payload.teamName),
    ],
    successMessage: 'Policy has been deleted',
  },
  platformSaveCustomColumn: {
    errorMessage: 'Failed to save custom column',
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformSaveCustomColumn>) =>
        getPlatformListAllCustomColumnsQueryKey(payload.teamName),
    ],
    successMessage: 'Custom column has been created and it will be populated shortly',
  },
  platformUpdateCustomColumn: {
    errorMessage: 'Failed to update custom column',
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformUpdateCustomColumn>) =>
        getPlatformListAllCustomColumnsQueryKey(payload.teamName),
    ],
    successMessage: 'Custom column has been updated and it will be re-populated shortly',
  },
  platformDeleteCustomColumn: {
    errorMessage: 'Failed to delete custom column',
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformDeleteCustomColumn>) =>
        getPlatformListAllCustomColumnsQueryKey(payload.teamName),
    ],
    successMessage: 'Custom column has been deleted',
  },
  platformDeleteReport: {
    errorMessage: 'Failed to delete report',
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformDeleteReport>) =>
        getPlatformListReportsQueryKey(payload.teamName),
    ],
    successMessage: 'Report has been deleted',
  },
  platformCreateReport: {
    errorMessage: 'Failed to create report',
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformCreateReport>) =>
        getPlatformListReportsQueryKey(payload.teamName),
    ],
    successMessage: 'Report has been created',
  },
  platformUpdateReport: {
    errorMessage: 'Failed to update report',
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformUpdateReport>) =>
        getPlatformListReportsQueryKey(payload.teamName),
    ],
    successMessage: 'Report has been updated',
  },
  platformUpdateSettings: {
    errorMessage: 'Failed to update settings',
    invalidateQueries: [getPlatformGetSettingsQueryKey()],
    successMessage: ({ payload }: MessageParams<typeof usePlatformUpdateSettings>) => {
      return payload.data.enforce_mfa
        ? 'Enabled Multi-factor authentication Enforcement'
        : 'Disabled Multi-factor authentication Enforcement';
    },
  },
  platformManageUserTOTPDelete: {
    errorMessage: 'Failed to reset MFA',
    invalidateQueries: [],
    successMessage: 'Multi-factor authentication Reset for selected user.',
  },
  platformUserTOTPDelete: {
    errorMessage: 'Failed to reset Multi-factor authentication.',
    invalidateQueries: [],
    successMessage: 'Multi-factor authentication Reset successfully.',
  },
  platformUserTOTPVerify: {
    errorMessage: 'Failed to verify Multi-factor authentication.',
    invalidateQueries: [],
    successMessage: null,
  },
  platformCreateV2SyncIntegration: {
    errorMessage: 'Failed to create integration',
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformCreateV2SyncIntegration>) =>
        getPlatformListV2SyncIntegrationsQueryKey(payload.teamName),
    ],
    successMessage: 'Integration has been created',
  },
  platformUpdateV2SyncIntegration: {
    errorMessage: 'Failed to update integration',
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformUpdateV2SyncIntegration>) =>
        getPlatformListV2SyncIntegrationsQueryKey(payload.teamName),
      ({ payload }: InvalidateQueryParams<typeof usePlatformUpdateV2SyncIntegration>) =>
        getPlatformGetV2SyncIntegrationQueryKey(payload.teamName, payload.syncName),
    ],
    successMessage: ({ payload }: MessageParams<typeof usePlatformUpdateV2SyncIntegration>) => {
      if (Object.keys(payload.data).length === 1 && payload.data.disabled === true) {
        return 'Integration has been disabled';
      } else if (Object.keys(payload.data).length === 1 && payload.data.disabled === false) {
        return 'Integration has been enabled';
      }

      return 'Integration has been updated';
    },
  },
  platformDeleteV2SyncIntegration: {
    errorMessage: 'Failed to delete integration',
    invalidateQueries: [
      ({ payload }: InvalidateQueryParams<typeof usePlatformDeleteV2SyncIntegration>) =>
        getPlatformListV2SyncIntegrationsQueryKey(payload.teamName),
      ({ payload }: InvalidateQueryParams<typeof usePlatformDeleteV2SyncIntegration>) =>
        getPlatformGetV2SyncIntegrationQueryKey(payload.teamName, payload.syncName),
    ],
    successMessage: 'Integration has been deleted',
  },
  platformHealthCheckHead: undefined,
  platformAssetViewTrigger: undefined,
  platformManageUpdateSAML: undefined,
  platformCreateLocalUserResetToken: undefined,
  platformCreatePlugin: undefined,
  platformUpdatePlugin: undefined,
  platformDeletePluginByTeamAndPluginName: undefined,
  platformCreatePluginVersion: undefined,
  platformUpdatePluginVersion: undefined,
  platformUploadPluginAsset: undefined,
  platformCreatePluginVersionDocs: undefined,
  platformReplacePluginVersionDocs: undefined,
  platformDeletePluginVersionDocs: undefined,
  platformCreatePluginVersionTables: undefined,
  platformDeletePluginVersionTables: undefined,
  platformUploadPluginUIAssets: undefined,
  platformFinalizePluginUIAssetUpload: undefined,
  platformRemovePluginUIAssets: undefined,
  platformTestUnsavedAlert: undefined,
  platformCreateConnector: undefined,
  platformUpdateConnector: undefined,
  platformRevokeConnector: undefined,
  platformAuthenticateConnectorAWS: undefined,
  platformAuthenticateConnectorFinishAWS: undefined,
  platformAuthenticateConnectorGCP: undefined,
  platformAuthenticateConnectorFinishGCP: undefined,
  platformAuthenticateConnectorOAuth: undefined,
  platformAuthenticateConnectorFinishOAuth: undefined,
  platformCreateConversation: undefined,
  platformSendMessage: undefined,
  platformPutCustomColumnData: undefined,
  platformPutCustomColumnValues: undefined,
  platformCreateTeamImages: undefined,
  platformDeleteTeamInvitation: undefined,
  platformAcceptTeamInvitation: undefined,
  platformRemoveTeamMembership: undefined,
  platformDeleteTeamMembership: undefined,
  platformTestUnsavedNotificationDestination: undefined,
  platformTestNotificationDestination: undefined,
  platformCreateAWSOnboarding: undefined,
  platformNotifyOnboarding: undefined,
  platformProvisionOnboardingConfiguration: undefined,
  platformDeletePluginsByTeam: undefined,
  platformTogglePolicy: undefined,
  platformDeleteAlert: undefined,
  platformExecuteSavedQueryTeam: undefined,
  platformQuerySaveFilterTeam: undefined,
  platformCreateReportTemplate: undefined,
  platformUpdateReportTemplate: undefined,
  platformDeleteReportTemplate: undefined,
  platformCreateSyncDestinationTestConnection: undefined,
  platformUpdateSyncTestConnectionForSyncDestination: undefined,
  platformUpdateSyncDestination: undefined,
  platformCreateSyncSourceTestConnection: undefined,
  platformUpdateSyncTestConnectionForSyncSource: undefined,
  platformCreateV2SyncIntegrationTestConnection: undefined,
  platformUpdateSyncSource: undefined,
  platformUpdateSyncTransformer: undefined,
  platformDeleteSyncTransformer: undefined,
  platformCreateSyncRunProgress: undefined,
  platformUploadImage: undefined,
  platformSendAnonymousEvent: undefined,
  platformAuthenticateLocalUser: undefined,
  platformUpdateCustomer: undefined,
  platformSendUserEvent: undefined,
  platformUserTOTPSetup: undefined,
  platformLoginUser: undefined,
  platformLogoutUser: undefined,
  platformResetLocalUserPassword: undefined,
  platformCreateUserToken: undefined,
  platformDeleteUser: undefined,
  platformCreateV2SyncDestinationTestConnection: undefined,
};
/* eslint-enable sort-keys-fix/sort-keys-fix */

function getInvalidateSyncSourceSyncsQueryKey(
  teamName: string,
  cachedQueries: Query[],
): { queryKeys: QueryKey[] } {
  return {
    queryKeys: cachedQueries
      .map(({ queryKey }) => {
        if (typeof queryKey[0] !== 'string') {
          return undefined;
        }

        const sourceSyncsQueryKeyRegex = new RegExp(
          `^${getPlatformListSyncSourceSyncsQueryKey(teamName, '.*')}$`,
        );

        return sourceSyncsQueryKeyRegex.test(queryKey[0]) ? queryKey : undefined;
      })
      .filter(Boolean),
  };
}

function getInvalidateSyncDestinationSyncsQueryKey(
  teamName: string,
  cachedQueries: Query[],
): { queryKeys: QueryKey[] } {
  return {
    queryKeys: cachedQueries
      .map(({ queryKey }) => {
        if (typeof queryKey[0] !== 'string') {
          return undefined;
        }

        const destinationSyncsQueryKeyRegex = new RegExp(
          `^${getPlatformListSyncDestinationSyncsQueryKey(teamName, '.*')}$`,
        );

        return destinationSyncsQueryKeyRegex.test(queryKey[0]) ? queryKey : undefined;
      })
      .filter(Boolean),
  };
}
